import { IcoEdit, IcoRemove } from '@/utils/icons'
import { selectProfile } from '@store/profile.reducer'
import {
  Dispatch,
  FC,
  forwardRef,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import setSettingsFromServer from '@/helpers/setSettingsFromServer'
import moment from 'moment'
import { Empty, Modal } from 'antd'
import cn from 'classnames'
import classes from './index.module.css'
import amclasses from '@pages/Admin/CRM/styles/ammodal.module.css'
import { useAddAlert } from '@/hooks/useAddAlert'
import { BodyModal } from '@common/ProfileCard/BodyModal/BodyModal'
import Preloader from '@UI/Preloader'
import { useAsyncRetry } from 'react-use'
import { classApi, crmApi } from '@/api'
import { LeadPayments } from '@/api/types/crm.types'
import { formatPrice, getLatestActivePayment } from './utils'
import Button from '@UI/New/Button'
import ModalRemoveClass from '@common/ProfileCard/BodyEdit/ModalRemoveClass'
import { closeModal } from '@store/modalsSlise'
import text from '@tiptap/extension-text'
import group from 'antd/es/avatar/group'
import ActionModal from '@pages/Admin/AllUsers/modals/ActionModal'
import { useLeadStatuses } from '../../../../blocks/StatusListBlock/LeadStatusContext'
import { useLeadRefetch } from '../../../../LeadRefetchContext'

interface CourseClassListProps {
  userId: number
  loading: boolean
  paymentData: LeadPayments
  refetchPaymentData: () => void
}

type ModalType = { type: string; data?: object } | null

const CourseClassList = ({
  userId,
  loading,
  paymentData,
  refetchPaymentData,
}: CourseClassListProps) => {
  const addAlert = useAddAlert()

  const [platformSettings, setSettings] = useState({})
  const [modal, setModal] = useState<ModalType>(null)

  useEffect(() => {
    setSettingsFromServer(setSettings)
  }, [])

  const userIdsAllowedToDeleteCourses =
    'userIdsAllowedToDeleteCourses' in platformSettings &&
    platformSettings.userIdsAllowedToDeleteCourses
      ? JSON.parse(platformSettings.userIdsAllowedToDeleteCourses as string)
      : []
  const selectEmployeesWhoAreAllowedDeleteCourses =
    'selectEmployeesWhoAreAllowedDeleteCourses' in platformSettings &&
    platformSettings.selectEmployeesWhoAreAllowedDeleteCourses === '1'

  const profile = useSelector(selectProfile)

  const canDeleteCourse =
    userIdsAllowedToDeleteCourses.includes(profile.id) ||
    !selectEmployeesWhoAreAllowedDeleteCourses

  return (
    <>
      {loading ? (
        <Preloader fullScreen="center" />
      ) : paymentData.classes.length < 1 &&
        paymentData.directions.length < 1 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={'Списко покупок пока ещё пуст'}
          className={classes.empty}
        />
      ) : (
        <div className={cn(classes.mainList, 'custom-scroll')}>
          <DirectionList
            directions={paymentData.directions}
            setModal={setModal}
            canDeleteCourse={canDeleteCourse}
            addAlert={addAlert}
            refetchList={refetchPaymentData}
            userId={userId}
          />
          <ClassesList
            classes={paymentData.classes}
            setModal={setModal}
            canDeleteCourse={canDeleteCourse}
            addAlert={addAlert}
            refetchList={refetchPaymentData}
            userId={userId}
          />
        </div>
      )}

      {/* Modals */}
      <Modal
        open={modal !== null}
        onOk={() => setModal(null)}
        onCancel={() => setModal(null)}
        footer={null}
        title={null}
        centered
        maskClosable
        className={cn(amclasses.modal)}
      >
        <BodyModal
          type={modal?.type || ''}
          data={modal?.data}
          state={{ id: userId }}
          setState={() => {}} // вроде не используется в AddDirection и RemoveDirection
          close={() => setModal(null)}
          setBodyModal={modal}
          addAlert={addAlert}
          getDirection={() => refetchPaymentData()}
        />
      </Modal>
    </>
  )
}

interface OtherListProps {
  setModal: Dispatch<SetStateAction<ModalType>>
  canDeleteCourse: boolean
  addAlert: (data: any) => void
  refetchList: () => void
  userId: number
}

const ClassesList: FC<
  {
    classes: LeadPayments['classes']
  } & OtherListProps
> = ({
  classes: classesData,
  setModal,
  addAlert,
  refetchList,
  userId,
  ...props
}) => {
  const [modalRemoveClass, setModalRemoveClass] = useState<any>({
    active: false,
    data: {},
  })
  const [modalDetachGroup, setModalDetachGroup] = useState<any>({
    isOpen: false,
  })

  return classesData?.map((paymentClass) => {
    // prettier-ignore
    const { class: classData, classUser, directions, payments } = paymentClass
    const payment = getLatestActivePayment(payments)
    return (
      <div key={`c${paymentClass.class.id}`} className={classes.classItem}>
        <div className={classes.classItemHeader}>
          <div className={classes.className}>
            <p>{classData.title}</p>
          </div>
          <div className={classes.classItemHeaderMetrics}>
            <DirectionAdditionalDescription
              title="Стоимость класса"
              value={`${formatPrice(Number(classData?.amount) ?? 0)} руб.`}
            />
            <DirectionAdditionalDescription
              title={payment.status === 1 || payment.is_manual === 1 ? "Сколько оплачено" : "В процессе оплаты"}
              value={`${formatPrice((Number(classUser?.amount || 0) + Number(classUser?.discount || 0)))} руб.`}
            />
            {payment?.buy_hours === null || payment?.buy_hours === undefined ? (
              <>
                <DirectionAdditionalDescription
                  title="Дата оплаты"
                  value={payment?.old_date ?? '-'}
                />
                <DirectionAdditionalDescription
                  value={`${payment?.old_date ?? 'Нету'} - ${
                    payment?.new_date ?? 'Нету'
                  }`}
                />
              </>
            ) : (
              <DirectionAdditionalDescription
                title="Кол часов"
                value={payment?.buy_hours.toString()}
              />
            )}
            <Button
              onClick={() =>
                setModal({
                  type: 'AddGroupToClass',
                  data: {
                    classId: classData.id,
                    selected: directions?.map(
                      ({ directionOrder }) => directionOrder.group_id,
                    ),
                    onSuccess: () => refetchList(),
                    onError: ({ data }: any) =>
                      addAlert({
                        type: 'error',
                        title: 'Не удалось добавить группу',
                        text: Object.entries(data.data)[0][1] || '',
                      }),
                  },
                })
              }
            >
              Добавить группу
            </Button>
            <Button
              color="danger"
              onClick={() =>
                setModalRemoveClass({
                  active: true,
                  data: {
                    user_id: userId,
                    elClass: classData,
                  },
                })
              }
            >
              Удалить класс
            </Button>
          </div>
        </div>
        <div className={classes.classDirectionList}>
          <DirectionList
            hiddenPaymentBlock
            hiddenEditButton
            detachDirection={(direction) =>
              setModalDetachGroup({
                isOpen: true,
                text: `Вы действительно хотите удалить ученика из группы "${direction.directionGroup.name}"?`,
                onConfirm: () => {
                  classApi
                    .detachUserClassGroup(classData.id, {
                      direction_group_id: direction.directionGroup.id,
                      user_id: userId,
                    })
                    .then(() => refetchList())
                  setModalDetachGroup({ isOpen: false })
                },
                onDismiss: () => setModalDetachGroup({ isOpen: false }),
              })
            }
            directions={directions}
            setModal={setModal}
            addAlert={addAlert}
            refetchList={refetchList}
            userId={userId}
            {...props}
          />
        </div>

        {/* Modals */}
        <Modal
          open={modalRemoveClass.active}
          onOk={() => setModalRemoveClass({ active: false, data: {} })}
          onCancel={() => setModalRemoveClass({ active: false, data: {} })}
          footer={null}
          title={null}
          centered
          maskClosable
          className={cn(amclasses.modal)}
        >
          <ModalRemoveClass
            closeModal={() => setModalRemoveClass({ active: false, data: {} })}
            elClass={modalRemoveClass.data.elClass}
            user_id={modalRemoveClass.data.user_id}
            setClasses={() => {}}
            fetchClasses={() => refetchList()}
          />
        </Modal>
        <Modal
          open={modalDetachGroup?.isOpen}
          onOk={() => setModalDetachGroup({ isOpen: false })}
          onCancel={() => setModalDetachGroup({ isOpen: false })}
          footer={null}
          title={null}
          centered
          maskClosable
          className={cn(amclasses.modal, classes.modalFullContent)}
        >
          <div>
            <ActionModal
              title={'Вы точно хотите открепить курс'}
              content={modalDetachGroup?.text}
              onDismiss={modalDetachGroup?.onDismiss}
              onAction={modalDetachGroup?.onConfirm}
            />
          </div>
        </Modal>
      </div>
    )
  })
}

const DirectionList: FC<
  {
    directions: LeadPayments['directions']
    hiddenPaymentBlock?: boolean
    hiddenEditButton?: boolean
    detachDirection?: (direction: LeadPayments['directions'][number]) => void
  } & OtherListProps
> = ({
  directions,
  hiddenPaymentBlock,
  hiddenEditButton,
  detachDirection,
  refetchList,
  setModal,
  canDeleteCourse,
}) =>
  directions?.map((paymentDirection) => {
    // prettier-ignore
    const { direction, directionOrder, directionOrderTariff, directionTeacher, directionGroup, directionGroupTeacher, payments, privateInfo } = paymentDirection
    const payment = getLatestActivePayment(payments)
    return (
      <div
        className={classes.directionListItem}
        key={`d${paymentDirection.direction.id}`}
      >
        <div className={classes.directionListItemHeaderInfo}>
          <div className={classes.directionInfoWrapper}>
            <div className={classes.directionInfoContainer}>
              <DirectionMainField
                direction={direction}
                directionOrderTariff={directionOrderTariff}
                directionTeacher={directionTeacher}
                directionGroup={directionGroup}
                directionGroupTeacher={directionGroupTeacher}
              />
            </div>
            {privateInfo?.salary_per_hour && (
              <div className={classes.directionInfoContainer}>
                <DirectionIndividualField privateInfo={privateInfo} />
              </div>
            )}
            {!hiddenPaymentBlock && (
              <div className={classes.directionInfoContainer}>
                <DirectionPaymentInfoField payment={payment} />
              </div>
            )}
          </div>
          <div className={classes.directionItemActions}>
            {!hiddenEditButton && privateInfo?.salary_per_hour && (
              <div
                onClick={() => {
                  setModal({
                    type: 'AddDirection',
                    data: {
                      item: {
                        ...directionOrder,
                        direction: {
                          ...direction,
                          directionTariffs: [directionOrderTariff],
                        },
                      },
                      privateInfo: privateInfo,
                      teacher: directionTeacher,
                    },
                  })
                }}
                className={classes.secondaryIcon}
              >
                <IcoEdit />
              </div>
            )}
            {canDeleteCourse && (
              <div
                onClick={() => {
                  !detachDirection
                    ? setModal({
                        type: 'RemoveDirection',
                        data: directionOrder.id as any,
                      })
                    : detachDirection(paymentDirection)
                }}
                className={classes.secondaryIcon}
              >
                <IcoRemove />
              </div>
            )}
          </div>
        </div>
      </div>
    )
  })

const DirectionMainField: FC<{
  direction: LeadPayments['directions'][number]['direction']
  directionOrderTariff: LeadPayments['directions'][number]['directionOrderTariff']
  directionTeacher: LeadPayments['directions'][number]['directionTeacher']
  directionGroup: LeadPayments['directions'][number]['directionGroup']
  directionGroupTeacher: LeadPayments['directions'][number]['directionGroupTeacher']
}> = ({
  direction,
  directionOrderTariff,
  directionTeacher,
  directionGroup,
  directionGroupTeacher,
}) => (
  <>
    <DirectionDescription
      title="Название"
      value={direction?.name ?? 'Названия нет'}
    />
    <DirectionDescription
      title="Тариф"
      value={directionOrderTariff.name ?? '-'}
    />
    <DirectionDescription
      title="Группа/Педагог"
      value={`${directionGroup?.name ?? ''} ${
        directionOrderTariff?.any_seminarian
          ? 'Любой учитель'
          : directionGroup !== null
          ? directionGroupTeacher?.name ?? 'Не распределено'
          : directionTeacher?.name ?? 'Не распределено'
      }`}
    />
  </>
)

const DirectionIndividualField: FC<{
  privateInfo: LeadPayments['directions'][number]['privateInfo']
}> = ({ privateInfo }) => (
  <div className={classes.directionAdditionalInfoContainer}>
    <DirectionAdditionalDescription
      title="Кол-во в неделю"
      value={privateInfo?.lessons_per_week?.toString() ?? '-'}
    />
    <DirectionAdditionalDescription
      title="Длительность/час"
      value={privateInfo?.time_one_lesson?.toString() ?? '-'}
    />
    <DirectionAdditionalDescription
      title="Цена/час"
      value={privateInfo?.hour_cost ? privateInfo?.hour_cost + ' руб.' : '-'}
    />
    <DirectionAdditionalDescription
      title="Педагог/час"
      value={
        privateInfo?.salary_per_hour
          ? privateInfo?.salary_per_hour + ' руб.'
          : '-'
      }
    />
  </div>
)

const DirectionPaymentInfoField: FC<{
  payment: LeadPayments['directions'][number]['payments'][number]
}> = ({ payment }) => (
  <>
    <DirectionAdditionalDescription
      title={payment.status === 1 || payment.is_manual === 1 ? "Оплачено" : "В процессе оплаты"}
      value={`${formatPrice(payment?.amount ?? 0)} руб.`}
    />
    {payment?.buy_hours === null || payment?.buy_hours === undefined ? (
      <>
        <DirectionAdditionalDescription
          title="Дата оплаты"
          value={payment?.old_date ?? '-'}
        />
        <DirectionAdditionalDescription
          value={`${payment?.old_date ?? 'Нету'} - ${
            payment?.new_date ?? 'Нету'
          }`}
        />
      </>
    ) : (
      <DirectionAdditionalDescription
        title="Кол часов"
        value={payment?.buy_hours.toString()}
      />
    )}
  </>
)

// ------ base ------

const DirectionAdditionalDescription: FC<{
  title?: string
  value?: string
}> = ({ title, value }) => (
  <div className={classes.directionAdditionalDescriptionWrapper}>
    <div
      className={cn(
        classes.directionAdditionalDescriptionTitle,
        classes.truncateText,
      )}
    >
      {title}
    </div>
    <div
      className={cn(
        classes.inputSmallContainer,
        classes.directionAdditionalDescriptionTitle,
      )}
    >
      {value}
    </div>
  </div>
)

const DirectionDescription: FC<{ title?: string; value?: string }> = ({
  title,
  value,
}) => (
  <div className={classes.directionDescriptionWrapper}>
    <div className={classes.directionDescriptionTitle}>{title}</div>
    <div className={cn(classes.inputSmallContainer, classes.truncateText)}>
      {value}
    </div>
  </div>
)

export default CourseClassList
